import React, { useState, useRef, useEffect } from 'react';
import { Text } from './text';
import { Button } from './button';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import {
  ArrowLeftIcon,
  MagnifyingGlassPlusIcon,
  MagnifyingGlassMinusIcon,
  ArrowDownTrayIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MapPinIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon
} from '@heroicons/react/24/solid';
import { useUser } from './UserContext';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { Dialog } from '@headlessui/react';
import { logBitacora } from '../utils/bitacora';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// Field definitions (copy from ProcessedFileDialog)
const ACTA_FIELDS = [
  { name: 'Razón Social', value: 'CompanyName' },
  { name: 'Fecha de Constitución', value: 'DateOfCompanyConstitution' },
  { name: 'Poderes Administrativos', value: 'AdministrationPower' },
  { name: 'Poderes de Dominio', value: 'DomainPowers' },
  { name: 'Ambos Poderes', value: 'BothPowers' },
  { name: 'Accionistas', value: 'ListOfShareholders' },
  { name: '% Accionistas', value: 'ShareholderStock' },
  { name: 'Exclusion de Extranjeros', value: 'ForigenersExclusionClause' },
];
const ACTA_ASAMBLEA_FIELDS = [
  { name: 'Razón Social', value: 'CompanyName' },
  { name: 'Fecha de Constitución', value: 'DateOfCompanyConstitution' },
  { name: 'Poderes Administrativos', value: 'AdministrationPower' },
  { name: 'Poderes de Dominio', value: 'DomainPowers' },
  { name: 'Ambos Poderes', value: 'BothPowers' },
  { name: 'Accionistas', value: 'ListOfShareholders' },
  { name: '% Accionistas', value: 'ShareholderStock' },
  { name: 'Exclusion de Extranjeros', value: 'ForigenersExclusionClause' },
  { name: 'Fecha de Notarización', value: 'NotarizationDate' },
];
const CSF_FIELDS = [
  { name: 'RFC', value: 'RFC' },
  { name: 'Nombre Legal de la Compañía', value: 'CompanyLegalName' },
  { name: 'Régimen Capital', value: 'CapitalRegime' },
  { name: 'Régimen Fiscal', value: 'TaxRegime' },
  { name: 'Domicilio Fiscal', value: 'FiscalAddress' },
  { name: 'Código Postal', value: 'PostalCode' },
  { name: 'Actividad Económica', value: 'EconomicActivity' },
  { name: 'Fecha de Inscripción', value: 'RegistrationDate' },
  { name: 'Status', value: 'Status' },
];
const COMPROBANTE_FIELDS = [
  { name: 'Nombre de la Compañía', value: 'CompanyName' },
  { name: 'Dirección de Servicio', value: 'ServiceAddress' },
  { name: 'Código Postal', value: 'PostalCode' },
  { name: 'Tipo de Documento', value: 'DocumentType' },
  { name: 'Fecha del Documento', value: 'DocumentDate' },
];
const IDENTIFICACION_FIELDS = [
  { name: 'Tipo de Documento', value: 'DocumentType' },
  { name: 'Nombre Completo', value: 'FullName' },
  { name: 'Fecha de Vencimiento', value: 'ExpirationDate' },
  { name: 'Número de Documento', value: 'DocumentNumber' },
  { name: 'Es Válido', value: 'IsValid' },
];
const ACUSE_FIELDS = [
  { name: 'Fecha de Presentación', value: 'PresentationDate' },
  { name: 'Folio de Recepción', value: 'ReceiptNumber' },
  { name: 'RFC', value: 'RFC' },
  { name: 'Razón Social', value: 'CompanyName' },
  { name: 'Tipo de Declaración', value: 'DeclarationType' },
  { name: 'Ejercicio Fiscal', value: 'FiscalYear' },
];
const DECLARACION_FIELDS = [
  { name: 'RFC', value: 'RFC' },
  { name: 'Razón Social', value: 'CompanyName' },
  { name: 'Ejercicio Fiscal', value: 'FiscalYear' },
  { name: 'Ingresos Totales', value: 'TotalIncome' },
  { name: 'Utilidad Fiscal', value: 'TaxableIncome' },
  { name: 'Impuesto Causado', value: 'TaxDue' },
  { name: 'Impuesto a Cargo', value: 'TaxPayable' },
];

const CONTRATO_FIELDS = [
  { name: 'Número de contrato', value: 'numeroContrato', optional: true },
  { name: 'Título del Contrato', value: 'tituloContrato' },
  { name: 'Tipo de Contrato', value: 'tipoContrato' },
  { name: 'Descripción', value: 'descripcion' },
  { name: 'Contratante', value: 'contratante' },
  { name: 'Proveedor', value: 'proveedor', optional: true },
  { name: 'Monto del Contrato', value: 'montoContrato' },
  { name: 'Fecha de Inicio', value: 'fechaInicio' },
  { name: 'Fecha de Término', value: 'fechaTermino' },
  { name: 'Fianzas Requeridas', value: 'fianzasRequeridas' },
  { name: 'Errores', value: 'errors' },
];

const ESTADOS_FINANCIEROS_CONTADOR_FIELDS = [
  { name: 'Total Activo', value: 'totalActivo' },
  { name: 'Total Pasivo', value: 'totalPasivo' },
  { name: 'Total Capital Contable', value: 'totalCapitalContable' },
  { name: 'Total Ingresos', value: 'totalIngresos' },
  { name: 'Total Gastos', value: 'totalGastos' },
  { name: 'Total Utilidad Antes de Impuestos', value: 'totalUtilidadAntesImpuestos' },
  { name: 'Total Utilidad Neta', value: 'totalUtilidadNeta' },
  { name: 'Año Fiscal', value: 'fiscalYear' },
  { name: 'Firma del Contador', value: 'hasAccountantSignature' },
  { name: 'Cédula Profesional del Contador', value: 'hasContadorCedulaProfesional' },
  { name: 'Texto de Responsabilidad del Contador', value: 'hasAccountantResponsabilityText' },
];

const ESTADOS_FINANCIEROS_PARCIALES_FIELDS = [
  { name: 'Periodo (Inicio)', value: 'periodoInicio' },
  { name: 'Periodo (Fin)', value: 'periodoFin' },
  ...ESTADOS_FINANCIEROS_CONTADOR_FIELDS,
];

const CEDULA_CONTADOR_FIELDS = [
  { name: 'Nombre Completo', value: 'NombreCompleto' },
  { name: 'Número de Cédula', value: 'NumeroCedula' },
  { name: 'Carrera o Título', value: 'CarreraOTitulo' },
  { name: 'Institución', value: 'Institucion' },
  { name: 'Fecha de Expedición', value: 'FechaExpedicion' },
  { name: 'Lugar de Expedición', value: 'LugarExpedicion' },
  { name: 'Tipo de Documento', value: 'TipoDocumento' },
  { name: 'URL de Verificación', value: 'URLVerificacion' },
];

const CURRICULUM_EMPRESARIAL_FIELDS = [
  { name: 'Nombre de la Empresa', value: 'NombreEmpresa' },
  { name: 'Resumen', value: 'Resumen' },
  { name: 'Servicios Principales', value: 'ServiciosPrincipales' },
  { name: 'Experiencia Destacada', value: 'ExperienciaDestacada' },
  { name: 'Equipo Clave', value: 'EquipoClave' },
  { name: 'Años de Experiencia', value: 'AniosExperiencia' },
  { name: 'Ubicación', value: 'Ubicacion' },
];

const PODERES_FIELDS = [
  { name: 'Razón Social', value: 'CompanyName' },
  { name: 'Poderdante', value: 'Poderdante' },
  { name: 'Poderes Administrativos', value: 'AdministrationPower' },
  { name: 'Poderes de Dominio', value: 'DomainPowers' },
  { name: 'Ambos Poderes', value: 'BothPowers' },
  { name: 'Nombre del Notario', value: 'NotaryName' },
  { name: 'Número del Notario', value: 'NotaryNumber' },
  { name: 'Número de Escritura', value: 'EscrituraNumber' },
  { name: 'Fecha de Notarización', value: 'NotarizationDate' },
  { name: 'Fecha de Vencimiento', value: 'ExpirationDate' },
  { name: 'Errores', value: 'errors' },
];

// --- Afianzadora document fields ---
const CONTRATO_MULTIPLE_FIELDS = [
  { name: 'Número de Contrato', value: 'numeroContrato' },
  { name: 'Aseguradora', value: 'aseguradora' },
  { name: 'Representante Aseguradora', value: 'representanteAseguradora' },
  { name: 'Fecha de Firma', value: 'fechaFirma' },
  { name: 'Fiado / Solicitante', value: 'fiadoSolicitante' },
  { name: 'Domicilio del Fiado', value: 'domicilioFiado' },
  { name: 'Representante del Fiado', value: 'representanteFiado' },
  { name: 'Obligados Solidarios', value: 'obligadosSolidarios' },
  { name: 'Garantías Inmobiliarias', value: 'garantiasInmobiliarias' },
  { name: 'Errores', value: 'errors' },
];

const REVISION_BURO_FIELDS = [
  { name: 'Tipo de Persona', value: 'tipoPersona' },
  { name: 'Nombre Completo', value: 'nombreCompleto' },
  { name: 'Razón Social', value: 'razonSocial' },
  { name: 'Representante Legal', value: 'representanteLegal' },
  { name: 'RFC', value: 'rfc' },
  { name: 'Calle y Número', value: 'calle' },
  { name: 'Colonia', value: 'colonia' },
  { name: 'Municipio', value: 'municipio' },
  { name: 'Estado', value: 'estado' },
  { name: 'Código Postal', value: 'codigoPostal' },
  { name: 'Fecha de Autorización', value: 'fechaAutorizacion' },
  { name: 'Firma Presente', value: 'firmaPresente' },
  { name: 'Errores', value: 'errors' },
];

const ENTREVISTA_AFIANZADORA_FIELDS = [
  { name: 'Tipo de Persona', value: 'tipoPersona' },
  { name: 'RFC', value: 'rfc' },
  { name: 'Nacionalidad', value: 'nacionalidad' },
  { name: 'Domicilio', value: 'domicilio' },
  { name: 'Teléfono', value: 'telefono' },
  { name: 'Correo Electrónico', value: 'correoElectronico' },
  { name: 'Nombre Completo', value: 'nombreCompleto' },
  { name: 'Fecha de Nacimiento', value: 'fechaNacimiento' },
  { name: 'CURP', value: 'curp' },
  { name: 'Ocupación', value: 'ocupacion' },
  { name: 'Sexo', value: 'sexo' },
  { name: 'Estado Civil', value: 'estadoCivil' },
  { name: 'Régimen Matrimonial', value: 'regimenMatrimonial' },
  { name: 'Razón Social', value: 'razonSocial' },
  { name: 'Giro Mercantil', value: 'giroMercantil' },
  { name: 'Fecha de Constitución', value: 'fechaConstitucion' },
  { name: 'Folio Mercantil', value: 'folioMercantil' },
  { name: 'Apoderado Legal', value: 'apoderadoLegal' },
  { name: 'Errores', value: 'errors' },
];

// --- Bien inmueble document fields ---
const ESCRITURA_FIELDS = [
  { name: 'Número de Escritura', value: 'numeroEscritura' },
  { name: 'Notario', value: 'notario' },
  { name: 'Fecha de Escritura', value: 'fechaEscritura' },
  { name: 'Ubicación del Inmueble', value: 'ubicacionInmueble' },
  { name: 'Superficie Terreno', value: 'superficieTerreno' },
  { name: 'Superficie Construcción', value: 'superficieConstruccion' },
  { name: 'Propietarios', value: 'propietarios' },
  { name: 'Valor de Escritura', value: 'valorEscritura' },
  { name: 'Gravámenes', value: 'gravamenes' },
  { name: 'Folio RPP', value: 'folioRPP' },
  { name: 'Errores', value: 'errors' },
];

const ACTA_MATRIMONIO_FIELDS = [
  { name: 'Nombre Cónyuge 1', value: 'nombreConyuge1' },
  { name: 'Nombre Cónyuge 2', value: 'nombreConyuge2' },
  { name: 'Fecha de Matrimonio', value: 'fechaMatrimonio' },
  { name: 'Régimen Matrimonial', value: 'regimenMatrimonial' },
  { name: 'Lugar de Registro', value: 'lugarRegistro' },
  { name: 'Número de Acta', value: 'numeroActa' },
  { name: 'Errores', value: 'errors' },
];

const BOLETA_PREDIAL_AVALUO_FIELDS = [
  { name: 'Cuenta Predial', value: 'cuentaPredial' },
  { name: 'Ubicación del Inmueble', value: 'ubicacionInmueble' },
  { name: 'Valor Catastral', value: 'valorCatastral' },
  { name: 'Valor Avalúo', value: 'valorAvaluo' },
  { name: 'Superficie Terreno', value: 'superficieTerreno' },
  { name: 'Superficie Construcción', value: 'superficieConstruccion' },
  { name: 'Año Fiscal', value: 'anioFiscal' },
  { name: 'Fecha Avalúo', value: 'fechaAvaluo' },
  { name: 'Perito', value: 'perito' },
  { name: 'Errores', value: 'errors' },
];

const LIBERTAD_GRAVAMEN_FIELDS = [
  { name: 'Folio', value: 'folio' },
  { name: 'Folio Real', value: 'folioReal' },
  { name: 'Fecha de Registro', value: 'fechaRegistro' },
  { name: 'Oficina', value: 'oficina' },
  { name: 'Propietario', value: 'propietario' },
  { name: 'Transmitente', value: 'transmitente' },
  { name: 'Ubicación del Inmueble', value: 'ubicacionInmueble' },
  { name: 'Superficie', value: 'superficie' },
  { name: 'Escritura', value: 'escritura' },
  { name: 'Notario', value: 'notario' },
  { name: 'Fecha de Operación', value: 'fechaOperacion' },
  { name: 'Libre de Gravamen', value: 'libreDeGravamen' },
  { name: 'Gravámenes Encontrados', value: 'gravamenesEncontrados' },
  { name: 'Fecha de Solicitud', value: 'fechaSolicitud' },
  { name: 'Fecha de Entrega', value: 'fechaEntrega' },
  { name: 'Errores', value: 'errors' },
];

const formatMontoForDisplay = (value) => {
  if (value === '' || value == null) return '';
  const num = typeof value === 'number' ? value : parseFloat(String(value).replace(/,/g, '')) || 0;
  if (!Number.isFinite(num) || num === 0) return '';
  return new Intl.NumberFormat('es-MX', { maximumFractionDigits: 2 }).format(num);
};

const MontoInput = ({ value, onChange, className = '', placeholder = '0.00' }) => {
  const [text, setText] = useState(() => formatMontoForDisplay(value));
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (focused) return;
    setText(formatMontoForDisplay(value));
  }, [value, focused]);

  const handleChange = (e) => {
    let cleaned = e.target.value.replace(/[^\d.]/g, '');
    const firstDot = cleaned.indexOf('.');
    if (firstDot !== -1) {
      cleaned = cleaned.slice(0, firstDot + 1) + cleaned.slice(firstDot + 1).replace(/\./g, '');
    }
    const [intPart, decPart] = cleaned.split('.');
    const limitedDec = decPart !== undefined ? decPart.slice(0, 2) : undefined;
    let display = '';
    if (intPart) display = Number(intPart).toLocaleString('es-MX');
    if (limitedDec !== undefined) display = (display || '0') + '.' + limitedDec;
    setText(display);
    const normalized = limitedDec !== undefined ? `${intPart || '0'}.${limitedDec}` : (intPart || '');
    const numeric = normalized === '' ? 0 : parseFloat(normalized) || 0;
    onChange(numeric);
  };

  return (
    <input
      type="text"
      inputMode="decimal"
      value={text}
      onChange={handleChange}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      placeholder={placeholder}
      className={className}
    />
  );
};

export function ProcessedFileView({
  isOpen,
  onClose,
  processedFileData,
  fileType,
  fileName,
  fileLabel,
  pdfUrl,
  linkId,
  contractId,
  onMarkAsReviewed,
  onUnmarkAsReviewed,
  onRefresh,
  bypassReview = false, // New parameter to show all fields even if not reviewed
  clientId,
  clientName,
  convenioId,
  convenioName,
}) {
  const [numPages, setNumPages] = useState(null);
  const [selectedLines, setSelectedLines] = useState([]);
  const [fieldIndices, setFieldIndices] = useState({});
  const [scale, setScale] = useState(1.0);
  const [localProcessedData, setLocalProcessedData] = useState(processedFileData);
  const pdfContainerRef = useRef(null);
  const { user, userData, isClient } = useUser();

  // New state for one-by-one field review
  const [currentFieldIndex, setCurrentFieldIndex] = useState(0);
  const [editingField, setEditingField] = useState(null); // fieldKey of field being edited
  const [editValue, setEditValue] = useState('');
  const [checkedFields, setCheckedFields] = useState(new Set()); // Track which fields have been checked
  const [editingFianzas, setEditingFianzas] = useState(null); // For editing fianzasRequeridas table
  const currentFieldRef = useRef(null);

  // Update local data when processedFileData changes
  useEffect(() => {
    setLocalProcessedData(processedFileData);
  }, [processedFileData]);

  // Reset field indices and review state when opening a new file
  useEffect(() => {
    if (isOpen) {
      setFieldIndices({});
      setSelectedLines([]);
      setCurrentFieldIndex(0); // Reset to first field
      setEditingField(null); // Clear any editing state
      setEditValue('');
      setCheckedFields(new Set()); // Clear checked fields
    }
  }, [isOpen, fileName]); // Reset when view opens or file changes

  // Hide body scroll when view is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Auto-scroll current review field to top of the panel
  useEffect(() => {
    if (currentFieldRef.current) {
      currentFieldRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [currentFieldIndex]);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };


  // Zoom handlers
  const handleZoomIn = () => {
    setScale(prev => Math.min(prev + 0.25, 3.0));
  };

  const handleZoomOut = () => {
    setScale(prev => Math.max(prev - 0.25, 0.5));
  };

  const handleResetZoom = () => {
    setScale(1.0);
  };

  // Download handler
  const handleDownload = async () => {
    if (pdfUrl) {
      try {
        // Fetch the PDF as a blob
        const response = await fetch(pdfUrl);
        const blob = await response.blob();

        // Create a blob URL
        const blobUrl = window.URL.createObjectURL(blob);

        // Create a temporary link and trigger download
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName || 'documento.pdf';
        document.body.appendChild(link);
        link.click();

        // Cleanup
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      } catch (error) {
        console.error('Error downloading file:', error);
        // Fallback: open in new tab if download fails
        window.open(pdfUrl, '_blank');
      }
    }
  };

  // Helper to format currency values
  const formatCurrency = (value) => {
    if (!value) return value;

    // Convert to string and remove all spaces
    const cleanValue = String(value).trim().replace(/\s+/g, '');

    // Check if it looks like a number (with optional commas, spaces, and decimals)
    // This will match: "85,144,482.00" or "85144482.00" or "1,898,310.00" etc.
    const numberPattern = /^-?[\d,]+\.?\d*$/;

    if (numberPattern.test(cleanValue)) {
      // Remove commas and parse as float
      const numValue = parseFloat(cleanValue.replace(/,/g, ''));

      // Check if it's a valid number
      if (!isNaN(numValue)) {
        // Format with currency
        return new Intl.NumberFormat('es-MX', {
          style: 'currency',
          currency: 'MXN',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(numValue);
      }
    }

    return value;
  };

  // Helper to check if a field should be formatted as currency
  const isCurrencyField = (fieldKey) => {
    const currencyFields = [
      'TotalIncome', 'TaxableIncome', 'TaxDue', 'TaxPayable',
      'montoContrato', 'totalActivo', 'totalPasivo',
      'totalCapitalContable', 'totalIngresos', 'totalGastos',
      'totalUtilidadAntesImpuestos', 'totalUtilidadNeta'
    ];
    return currencyFields.includes(fieldKey);
  };

  // Helper to get fields for current file type
  const getFieldsForFileType = () => {
    switch (fileType) {
      case 'constanciaSituacionFiscal':
        return CSF_FIELDS;
      case 'contrato':
        return CONTRATO_FIELDS;
      case 'actaConstitutiva':
        return ACTA_FIELDS;
      case 'comprobanteDomicilio':
        return COMPROBANTE_FIELDS;
      case 'identificacionOficial':
        return IDENTIFICACION_FIELDS;
      case 'actasAsambleas':
        return ACTA_ASAMBLEA_FIELDS;
      case 'acuseReciboDeclaracion':
        return ACUSE_FIELDS;
      case 'declaracionEjercicio':
        return DECLARACION_FIELDS;
      case 'estadosFinancieros':
        return ESTADOS_FINANCIEROS_CONTADOR_FIELDS;
      case 'estadosFinancierosParciales':
        return ESTADOS_FINANCIEROS_PARCIALES_FIELDS;
      case 'cedulaContador':
        return CEDULA_CONTADOR_FIELDS;
      case 'curriculumEmpresarial':
        return CURRICULUM_EMPRESARIAL_FIELDS;
      case 'poderes':
        return PODERES_FIELDS;
      case 'contratoMultiple':
        return CONTRATO_MULTIPLE_FIELDS;
      case 'revisionBuro':
        return REVISION_BURO_FIELDS;
      case 'entrevista':
        return ENTREVISTA_AFIANZADORA_FIELDS;
      case 'escritura':
        return ESCRITURA_FIELDS;
      case 'actaMatrimonio':
        return ACTA_MATRIMONIO_FIELDS;
      case 'boletaPredialAvaluo':
        return BOLETA_PREDIAL_AVALUO_FIELDS;
      case 'libertadGravamen':
        return LIBERTAD_GRAVAMEN_FIELDS;
      default:
        return [];
    }
  };

  // Handle field edit mode
  const handleStartEdit = (fieldKey, currentValue) => {
    setEditingField(fieldKey);
    setEditValue(currentValue || '');
  };

  // Handle save edit
  const handleSaveEdit = async (fieldKey) => {
    if (!localProcessedData?.results) return;

    // Preserve original value if this is the first edit
    const fieldData = localProcessedData.results[fieldKey];
    const currentValue = fieldData?.value;

    const updatedFieldData = !fieldData?.originalValue && currentValue !== editValue
      ? {
          ...localProcessedData.results[fieldKey],
          value: editValue,
          originalValue: currentValue, // Preserve the original
          editedBy: user?.email || 'Usuario',
          editedAt: Date.now()
        }
      : {
          ...localProcessedData.results[fieldKey],
          value: editValue,
          editedBy: user?.email || 'Usuario',
          editedAt: Date.now()
        };

    // Update local state
    setLocalProcessedData(prev => ({
      ...prev,
      results: {
        ...prev.results,
        [fieldKey]: updatedFieldData
      }
    }));

    // Save to database
    if (linkId && processedFileData?.fileDocId) {
      try {
        const fileDocRef = doc(db, "links", linkId, "files", processedFileData.fileDocId);
        await updateDoc(fileDocRef, {
          [`results.${fieldKey}`]: updatedFieldData
        });
      } catch (error) {
        console.error('Error saving field edit to database:', error);
      }
    }

    // Mark this field as checked
    setCheckedFields(prev => new Set([...prev, fieldKey]));

    // Exit edit mode
    setEditingField(null);
    setEditValue('');
  };

  // Handle field navigation (move to next field)
  const handleNextField = () => {
    const fields = getFieldsForFileType();

    // Mark current field as checked
    const currentField = fields[currentFieldIndex];
    setCheckedFields(prev => new Set([...prev, currentField.value]));

    if (currentFieldIndex < fields.length - 1) {
      // Move to next field
      setCurrentFieldIndex(currentFieldIndex + 1);
    } else {
      // Last field - all checked, auto-mark as reviewed
      const updatedChecked = new Set([...checkedFields, currentField.value]);
      if (updatedChecked.size === fields.length && !localProcessedData?.reviewedBy) {
        handleAutoMarkAsReviewed();
      }
    }
  };

  // Auto-mark as reviewed when all fields checked
  const handleAutoMarkAsReviewed = async () => {
    const hasLinkPath = linkId && processedFileData?.fileDocId;
    const hasContractPath = contractId && processedFileData?.fileDocId;

    if (onMarkAsReviewed && (hasLinkPath || hasContractPath)) {
      try {
        if (hasLinkPath) {
          await onMarkAsReviewed(linkId, processedFileData.fileDocId);
        } else if (hasContractPath) {
          await onMarkAsReviewed(contractId, processedFileData.fileDocId);
        }

        // Update local state to show as reviewed
        setLocalProcessedData(prev => ({
          ...prev,
          reviewedBy: user?.email || 'Usuario',
          reviewedAt: new Date()
        }));

        // Log to bitacora
        try {
          const description = `Aceptó documento "${fileLabel || fileName}" después de revisar todos los campos`;
          await logBitacora({
            action: 'UPDATE',
            collection: hasLinkPath ? 'links_files' : 'contracts',
            documentId: processedFileData.fileDocId,
            metadata: convenioId
              ? {
                  convenioId,
                  convenioName: convenioName || null,
                  contractId,
                  description,
                }
              : {
                  linkId,
                  contractId,
                  clientId,
                  clientName: clientName || 'Sin nombre',
                  description,
                },
            user,
            userData,
          });
        } catch (logError) {
          console.error('Failed to log file review to bitacora:', logError);
        }

        // Optional: call onRefresh to update parent component
        if (onRefresh) {
          await onRefresh();
        }
      } catch (error) {
        console.error('Error auto-marking as reviewed:', error);
      }
    }
  };

  // Helper to render a field with clickable navigation for its sources
  const renderField = (label, fieldData, fieldKey, isReviewMode = false) => {
    const fieldValue = fieldData?.[fieldKey];
    let sources =
      (Array.isArray(fieldValue?.errorMessage?.sources) && fieldValue.errorMessage.sources) ||
      (Array.isArray(fieldValue?.sources) && fieldValue.sources) ||
      (Array.isArray(localProcessedData?.errors?.errorMessage?.sources) && localProcessedData.errors.errorMessage.sources) ||
      [];
    const currentIndex = fieldIndices[fieldKey] ?? 0;
    if (fieldKey === 'errors') {
      const errorMessage =
        fieldValue?.errorMessage?.value ||
        fieldValue?.value ||
        localProcessedData?.errors?.errorMessage?.value ||
        'N/A';

      // Check if there are actually errors (not just "N/A")
      const hasErrors = errorMessage !== 'N/A' && errorMessage !== '' && errorMessage !== null;

      return (
        <div className={`mb-4 p-3 rounded-lg border transition-colors ${
          hasErrors
            ? 'bg-red-50 border-red-200 hover:border-red-300'
            : 'bg-green-50 border-green-200 hover:border-green-300'
        }`}>
          <div className="flex items-center justify-between mb-2">
            <h3 className={`font-semibold ${hasErrors ? 'text-red-800' : 'text-green-800'}`}>{label}</h3>
            {sources.length > 0 && (
              <div className="flex items-center gap-1">
                {sources.length > 1 ? (
                  <>
                    <button
                      onClick={() => handleSourceNavigation(fieldKey, 'prev', sources)}
                      className={`p-1 rounded transition-colors ${hasErrors ? 'hover:bg-red-100' : 'hover:bg-green-100'}`}
                      title="Fuente anterior"
                    >
                      <ChevronLeftIcon className={`h-4 w-4 ${hasErrors ? 'text-red-700' : 'text-green-700'}`} />
                    </button>
                    <span className={`text-xs font-medium px-2 ${hasErrors ? 'text-red-700' : 'text-green-700'}`}>
                      {currentIndex + 1}/{sources.length}
                    </span>
                    <button
                      onClick={() => handleSourceNavigation(fieldKey, 'next', sources)}
                      className={`p-1 rounded transition-colors ${hasErrors ? 'hover:bg-red-100' : 'hover:bg-green-100'}`}
                      title="Siguiente fuente"
                    >
                      <ChevronRightIcon className={`h-4 w-4 ${hasErrors ? 'text-red-700' : 'text-green-700'}`} />
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => scrollToLines(sources)}
                    className={`flex items-center gap-1 px-2 py-1 rounded transition-colors ${hasErrors ? 'hover:bg-red-100' : 'hover:bg-green-100'}`}
                    title="Ver en el PDF"
                  >
                    <MapPinIcon className={`h-4 w-4 ${hasErrors ? 'text-red-700' : 'text-green-700'}`} />
                    <span className={`text-xs font-medium ${hasErrors ? 'text-red-700' : 'text-green-700'}`}>Ver fuente</span>
                  </button>
                )}
              </div>
            )}
          </div>
          <div className={`text-sm whitespace-pre-line ${hasErrors ? 'text-red-800' : 'text-green-800'}`}>
            {hasErrors ? errorMessage : 'Sin errores detectados'}
          </div>
          {isReviewMode && (
            <button
              onClick={handleNextField}
              className={`mt-3 w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                hasErrors
                  ? 'bg-red-600 text-white hover:bg-red-700'
                  : 'bg-green-600 text-white hover:bg-green-700'
              }`}
            >
              <CheckIcon className="h-5 w-5" />
              {hasErrors ? 'Aceptar' : 'Continuar'}
            </button>
          )}
        </div>
      );
    }
    if (fieldKey === 'fianzasRequeridas') {
      const fianzasArr = fieldData?.[fieldKey]?.value || [];
      const isEditing = editingField === fieldKey;

      if (!Array.isArray(fianzasArr) || fianzasArr.length === 0) {
        return (
          <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200 hover:border-azul-surelia/30 transition-colors">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-gray-800">{label}</h3>
              {sources.length > 0 && (
                <div className="flex items-center gap-1">
                  {sources.length > 1 ? (
                    <>
                      <button
                        onClick={() => handleSourceNavigation(fieldKey, 'prev', sources)}
                        className="p-1 hover:bg-azul-surelia/10 rounded transition-colors"
                        title="Fuente anterior"
                      >
                        <ChevronLeftIcon className="h-4 w-4 text-azul-surelia" />
                      </button>
                      <span className="text-xs font-medium text-azul-surelia px-2">
                        {currentIndex + 1}/{sources.length}
                      </span>
                      <button
                        onClick={() => handleSourceNavigation(fieldKey, 'next', sources)}
                        className="p-1 hover:bg-azul-surelia/10 rounded transition-colors"
                        title="Siguiente fuente"
                      >
                        <ChevronRightIcon className="h-4 w-4 text-azul-surelia" />
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => scrollToLines(sources)}
                      className="flex items-center gap-1 px-2 py-1 hover:bg-azul-surelia/10 rounded transition-colors"
                      title="Ver en el PDF"
                    >
                      <MapPinIcon className="h-4 w-4 text-azul-surelia" />
                      <span className="text-xs font-medium text-azul-surelia">Ver fuente</span>
                    </button>
                  )}
                </div>
              )}
            </div>
            <p className="text-gray-700">No hay fianzas requeridas.</p>
            {isReviewMode && (
              <button
                onClick={handleNextField}
                className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-2 bg-azul-surelia text-white rounded-lg hover:bg-azul-surelia/90 transition-colors"
              >
                <CheckIcon className="h-5 w-5" />
                Aceptar
              </button>
            )}
          </div>
        );
      }

      const handleEditFianzas = () => {
        setEditingFianzas(fianzasArr.map((f) => ({ ...f })));
        setEditingField(fieldKey);
      };

      const handleSaveFianzas = async () => {
        if (!editingFianzas || !localProcessedData?.results) return;

        // Get the current field data
        const fieldData = localProcessedData.results[fieldKey];
        const currentValue = fieldData?.value;

        // Build the updated field data structure
        const updatedFieldData = !fieldData?.originalValue && JSON.stringify(currentValue) !== JSON.stringify(editingFianzas)
          ? {
              ...fieldData,
              value: editingFianzas,
              originalValue: currentValue, // Preserve the original
              editedBy: user?.email || 'Usuario',
              editedAt: Date.now()
            }
          : {
              ...fieldData,
              value: editingFianzas,
              editedBy: user?.email || 'Usuario',
              editedAt: Date.now()
            };

        // Update local state
        setLocalProcessedData(prev => ({
          ...prev,
          results: {
            ...prev.results,
            [fieldKey]: updatedFieldData
          }
        }));

        // Save to database
        const hasLinkPath = linkId && processedFileData?.fileDocId;
        const hasContractPath = contractId && processedFileData?.fileDocId;

        if (hasLinkPath || hasContractPath) {
          try {
            if (hasContractPath) {
              // Convenio contract: update procesamiento subdoc + contract doc
              const procRef = doc(db, "contracts", contractId, "procesamiento", processedFileData.fileDocId);
              await updateDoc(procRef, {
                [`results.${fieldKey}`]: updatedFieldData
              });
              const contractDocRef = doc(db, "contracts", contractId);
              await updateDoc(contractDocRef, {
                fianzasRequeridas: updatedFieldData,
                updatedAt: Date.now()
              });
            } else {
              // Link-based: update file doc
              const fileDocRef = doc(db, "links", linkId, "files", processedFileData.fileDocId);
              await updateDoc(fileDocRef, {
                [`results.${fieldKey}`]: updatedFieldData
              });

              // Also update the corresponding contract document
              if (fileType === 'contrato') {
                const timestamp = processedFileData.fileDocId.split('_')[1];
                if (timestamp) {
                  const cId = `${linkId}_${timestamp}`;
                  const contractDocRef = doc(db, "contracts", cId);
                  await updateDoc(contractDocRef, {
                    fianzasRequeridas: updatedFieldData,
                    updatedAt: Date.now()
                  });
                }
              }
            }

            if (onRefresh) {
              await onRefresh();
            }
          } catch (error) {
            console.error('Error saving fianzas to database:', error);
          }
        }

        // Mark this field as checked
        setCheckedFields(prev => new Set([...prev, fieldKey]));

        // Close dialog
        setEditingFianzas(null);
        setEditingField(null);
      };

      const handleCancelEdit = () => {
        setEditingFianzas(null);
        setEditingField(null);
      };

      const updateFianza = (index, field, value) => {
        const nextValue = field === 'porcentaje'
          ? (parseFloat(value) || 0)
          : field === 'monto'
          ? (typeof value === 'number' ? value : parseFloat(value) || 0)
          : value;
        setEditingFianzas((prev) =>
          prev.map((row, i) => (i === index ? { ...row, [field]: nextValue } : row))
        );
      };

      return (
        <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200 hover:border-azul-surelia/30 transition-colors">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-gray-800">{label}</h3>
            <div className="flex items-center gap-2">
              {/* Edit button */}
              {!isEditing && !isClient && (
                <button
                  onClick={handleEditFianzas}
                  className="p-1.5 hover:bg-azul-surelia/10 rounded transition-colors"
                  title="Editar fianzas"
                >
                  <PencilIcon className="h-4 w-4 text-azul-surelia" />
                </button>
              )}

              {/* Source navigation */}
              {sources.length > 0 && (
                <div className="flex items-center gap-1">
                  {sources.length > 1 ? (
                    <>
                      <button
                        onClick={() => handleSourceNavigation(fieldKey, 'prev', sources)}
                        className="p-1 hover:bg-azul-surelia/10 rounded transition-colors"
                        title="Fuente anterior"
                      >
                        <ChevronLeftIcon className="h-4 w-4 text-azul-surelia" />
                      </button>
                      <button
                        onClick={() => scrollToLines([sources[currentIndex]])}
                        className="text-xs font-medium text-azul-surelia px-2 hover:bg-azul-surelia/10 rounded transition-colors cursor-pointer"
                        title="Ver fuente actual"
                      >
                        {currentIndex + 1}/{sources.length}
                      </button>
                      <button
                        onClick={() => handleSourceNavigation(fieldKey, 'next', sources)}
                        className="p-1 hover:bg-azul-surelia/10 rounded transition-colors"
                        title="Siguiente fuente"
                      >
                        <ChevronRightIcon className="h-4 w-4 text-azul-surelia" />
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => scrollToLines(sources)}
                      className="flex items-center gap-1 px-2 py-1 hover:bg-azul-surelia/10 rounded transition-colors"
                      title="Ver en el PDF"
                    >
                      <MapPinIcon className="h-4 w-4 text-azul-surelia" />
                      <span className="text-xs font-medium text-azul-surelia">Ver fuente</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-lg overflow-hidden shadow-sm border border-gray-200">
            <div className="overflow-x-auto overflow-y-auto max-h-96">
              <table className="min-w-full text-sm">
                <thead className="sticky top-0 z-10">
                  <tr className="bg-azul-surelia text-white">
                    <th className="px-4 py-3 text-left font-semibold">Tipo</th>
                    <th className="px-4 py-3 text-left font-semibold">Porcentaje</th>
                    <th className="px-4 py-3 text-left font-semibold">Monto</th>
                  </tr>
                </thead>
                <tbody>
                  {fianzasArr.map((f, idx) => (
                    <tr key={idx} className={`border-b border-gray-200 ${idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'} hover:bg-azul-surelia/5 transition-colors`}>
                      <td className="px-4 py-3">
                        <span className="text-gray-800">{f.tipo}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-azul-surelia font-medium">{f.porcentaje}%</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-gray-800 font-semibold">
                          {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(f.monto)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Aceptar button in review mode */}
          {isReviewMode && (
            <button
              onClick={handleNextField}
              className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-2 bg-azul-surelia text-white rounded-lg hover:bg-azul-surelia/90 transition-colors"
            >
              <CheckIcon className="h-5 w-5" />
              Aceptar
            </button>
          )}

          {/* Edit Dialog */}
          <Dialog open={isEditing && editingFianzas !== null} onClose={handleCancelEdit} className="relative z-50">
            <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
            <div className="fixed inset-0 flex items-center justify-center p-4">
              <Dialog.Panel className="mx-auto max-w-4xl w-full bg-white rounded-lg shadow-xl">
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                  <Dialog.Title className="text-xl font-semibold text-azul-surelia">
                    Editar Fianzas Requeridas
                  </Dialog.Title>
                  <button
                    onClick={handleCancelEdit}
                    className="p-1 hover:bg-gray-100 rounded transition-colors"
                  >
                    <XMarkIcon className="h-6 w-6 text-gray-500" />
                  </button>
                </div>

                <div className="p-6 max-h-[70vh] overflow-y-auto">
                  <table className="min-w-full">
                    <thead>
                      <tr className="bg-azul-surelia text-white">
                        <th className="px-4 py-3 text-left font-semibold">Tipo</th>
                        <th className="px-4 py-3 text-left font-semibold">Porcentaje</th>
                        <th className="px-4 py-3 text-left font-semibold">Monto</th>
                        <th className="px-4 py-3 w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {editingFianzas?.map((f, idx) => (
                        <tr key={idx} className={`border-b border-gray-200 ${idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}>
                          <td className="px-4 py-4">
                            <input
                              type="text"
                              value={f.tipo || ''}
                              onChange={(e) => updateFianza(idx, 'tipo', e.target.value)}
                              placeholder="Ej. Cumplimiento"
                              className="w-full px-3 py-2 border border-azul-surelia/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-azul-surelia/50"
                            />
                          </td>
                          <td className="px-4 py-4">
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                value={f.porcentaje}
                                onChange={(e) => updateFianza(idx, 'porcentaje', e.target.value)}
                                className="w-24 px-3 py-2 border border-azul-surelia/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-azul-surelia/50"
                              />
                              <span className="text-gray-600 font-medium">%</span>
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none">$</span>
                              <MontoInput
                                value={f.monto}
                                onChange={(num) => updateFianza(idx, 'monto', num)}
                                className="w-full pl-7 pr-14 py-2 border border-azul-surelia/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-azul-surelia/50"
                              />
                              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500 pointer-events-none">MXN</span>
                            </div>
                          </td>
                          <td className="px-2 py-4">
                            <button
                              onClick={() => {
                                const updated = editingFianzas.filter((_, i) => i !== idx);
                                setEditingFianzas(updated);
                              }}
                              className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                              title="Eliminar"
                            >
                              <XMarkIcon className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="flex flex-col gap-3 p-6 border-t border-gray-200">
                  <button
                    onClick={() => {
                      setEditingFianzas((prev) => [
                        ...(prev || []),
                        { tipo: '', porcentaje: 0, monto: 0, estatus: 'pendiente' },
                      ]);
                    }}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 border-2 border-dashed border-azul-surelia/30 text-azul-surelia rounded-lg hover:bg-azul-surelia/5 transition-colors text-sm font-medium"
                  >
                    + Agregar Fianza Requerida
                  </button>
                  <div className="flex gap-3">
                    <button
                      onClick={handleSaveFianzas}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-azul-surelia text-white rounded-lg hover:bg-azul-surelia/90 transition-colors font-medium"
                    >
                      <CheckIcon className="h-5 w-5" />
                      Guardar Cambios
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              </Dialog.Panel>
            </div>
          </Dialog>
        </div>
      );
    }
    // Obligados Solidarios - array of objects with per-item sources
    if (fieldKey === 'obligadosSolidarios') {
      const items = Array.isArray(fieldValue) ? fieldValue : [];
      return (
        <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200 hover:border-azul-surelia/30 transition-colors">
          <h3 className="font-semibold text-gray-800 mb-2">{label}</h3>
          {items.length === 0 ? (
            <p className="text-gray-500">No se encontraron obligados solidarios.</p>
          ) : (
            <div className="space-y-3">
              {items.map((os, idx) => {
                const itemSources = Array.isArray(os.sources) ? os.sources : [];
                const sourceKey = `${fieldKey}_${idx}`;
                const itemSourceIndex = fieldIndices[sourceKey] ?? 0;
                return (
                  <div key={idx} className="p-3 bg-white rounded-lg border border-gray-200">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-gray-800">{os.nombre || 'N/A'}</p>
                      {itemSources.length > 0 && (
                        <div className="flex items-center gap-1">
                          {itemSources.length > 1 ? (
                            <>
                              <button
                                onClick={() => handleSourceNavigation(sourceKey, 'prev', itemSources)}
                                className="p-1 hover:bg-azul-surelia/10 rounded transition-colors"
                                title="Fuente anterior"
                              >
                                <ChevronLeftIcon className="h-4 w-4 text-azul-surelia" />
                              </button>
                              <span className="text-xs font-medium text-azul-surelia px-2">
                                {itemSourceIndex + 1}/{itemSources.length}
                              </span>
                              <button
                                onClick={() => handleSourceNavigation(sourceKey, 'next', itemSources)}
                                className="p-1 hover:bg-azul-surelia/10 rounded transition-colors"
                                title="Siguiente fuente"
                              >
                                <ChevronRightIcon className="h-4 w-4 text-azul-surelia" />
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={() => scrollToLines(itemSources)}
                              className="flex items-center gap-1 px-2 py-1 hover:bg-azul-surelia/10 rounded transition-colors"
                              title="Ver en el PDF"
                            >
                              <MapPinIcon className="h-4 w-4 text-azul-surelia" />
                              <span className="text-xs font-medium text-azul-surelia">Ver fuente</span>
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                    {os.domicilio && (
                      <p className="text-xs text-gray-600 mt-1">
                        <span className="font-medium">Domicilio:</span> {os.domicilio}
                      </p>
                    )}
                    {os.representante && (
                      <p className="text-xs text-gray-600 mt-1">
                        <span className="font-medium">Representante:</span> {os.representante}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {isReviewMode && (
            <button
              onClick={handleNextField}
              className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-2 bg-azul-surelia text-white rounded-lg hover:bg-azul-surelia/90 transition-colors"
            >
              <CheckIcon className="h-5 w-5" />
              Aceptar
            </button>
          )}
        </div>
      );
    }
    // Garantías Inmobiliarias - array of objects with per-item sources
    if (fieldKey === 'garantiasInmobiliarias') {
      const items = Array.isArray(fieldValue) ? fieldValue : [];
      return (
        <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200 hover:border-azul-surelia/30 transition-colors">
          <h3 className="font-semibold text-gray-800 mb-2">{label}</h3>
          {items.length === 0 ? (
            <p className="text-gray-500">No se encontraron garantías inmobiliarias.</p>
          ) : (
            <div className="space-y-3">
              {items.map((gi, idx) => {
                const itemSources = Array.isArray(gi.sources) ? gi.sources : [];
                const sourceKey = `${fieldKey}_${idx}`;
                const itemSourceIndex = fieldIndices[sourceKey] ?? 0;
                return (
                  <div key={idx} className="p-3 bg-white rounded-lg border border-gray-200">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-800">Garantía {idx + 1}</span>
                      {itemSources.length > 0 && (
                        <div className="flex items-center gap-1">
                          {itemSources.length > 1 ? (
                            <>
                              <button
                                onClick={() => handleSourceNavigation(sourceKey, 'prev', itemSources)}
                                className="p-1 hover:bg-azul-surelia/10 rounded transition-colors"
                                title="Fuente anterior"
                              >
                                <ChevronLeftIcon className="h-4 w-4 text-azul-surelia" />
                              </button>
                              <span className="text-xs font-medium text-azul-surelia px-2">
                                {itemSourceIndex + 1}/{itemSources.length}
                              </span>
                              <button
                                onClick={() => handleSourceNavigation(sourceKey, 'next', itemSources)}
                                className="p-1 hover:bg-azul-surelia/10 rounded transition-colors"
                                title="Siguiente fuente"
                              >
                                <ChevronRightIcon className="h-4 w-4 text-azul-surelia" />
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={() => scrollToLines(itemSources)}
                              className="flex items-center gap-1 px-2 py-1 hover:bg-azul-surelia/10 rounded transition-colors"
                              title="Ver en el PDF"
                            >
                              <MapPinIcon className="h-4 w-4 text-azul-surelia" />
                              <span className="text-xs font-medium text-azul-surelia">Ver fuente</span>
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                    {gi.ubicacion && (
                      <p className="text-sm text-gray-800">
                        <span className="font-medium">Ubicación:</span> {gi.ubicacion}
                      </p>
                    )}
                    {gi.valorComercial && (
                      <p className="text-xs text-gray-600 mt-1">
                        <span className="font-medium">Valor Comercial:</span> {gi.valorComercial}
                      </p>
                    )}
                    {gi.datosRPP && (
                      <p className="text-xs text-gray-600 mt-1">
                        <span className="font-medium">Datos RPP:</span> {gi.datosRPP}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {isReviewMode && (
            <button
              onClick={handleNextField}
              className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-2 bg-azul-surelia text-white rounded-lg hover:bg-azul-surelia/90 transition-colors"
            >
              <CheckIcon className="h-5 w-5" />
              Aceptar
            </button>
          )}
        </div>
      );
    }
    const isEditing = editingField === fieldKey;
    const displayValue = Array.isArray(fieldValue?.value)
      ? fieldValue.value.join(', ')
      : isCurrencyField(fieldKey)
        ? formatCurrency(fieldValue?.value) || 'N/A'
        : fieldValue?.value || 'N/A';

    const originalValue = fieldValue?.originalValue;

    return (
      <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200 hover:border-azul-surelia/30 transition-colors">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold text-gray-800">{label}</h3>
          <div className="flex items-center gap-2">
            {/* Edit button */}
            {!isEditing && !isClient && (
              <button
                onClick={() => handleStartEdit(fieldKey, displayValue === 'N/A' ? '' : displayValue)}
                className="p-1 hover:bg-azul-surelia/10 rounded transition-colors"
                title="Editar campo"
              >
                <PencilIcon className="h-4 w-4 text-azul-surelia" />
              </button>
            )}

            {/* Source navigation */}
            {sources.length > 0 && (
              <div className="flex items-center gap-1">
                {sources.length > 1 ? (
                  <>
                    <button
                      onClick={() => handleSourceNavigation(fieldKey, 'prev', sources)}
                      className="p-1 hover:bg-azul-surelia/10 rounded transition-colors"
                      title="Fuente anterior"
                    >
                      <ChevronLeftIcon className="h-4 w-4 text-azul-surelia" />
                    </button>
                    <span className="text-xs font-medium text-azul-surelia px-2">
                      {currentIndex + 1}/{sources.length}
                    </span>
                    <button
                      onClick={() => handleSourceNavigation(fieldKey, 'next', sources)}
                      className="p-1 hover:bg-azul-surelia/10 rounded transition-colors"
                      title="Siguiente fuente"
                    >
                      <ChevronRightIcon className="h-4 w-4 text-azul-surelia" />
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => scrollToLines(sources)}
                    className="flex items-center gap-1 px-2 py-1 hover:bg-azul-surelia/10 rounded transition-colors"
                    title="Ver en el PDF"
                  >
                    <MapPinIcon className="h-4 w-4 text-azul-surelia" />
                    <span className="text-xs font-medium text-azul-surelia">Ver fuente</span>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Value display or edit mode */}
        {isEditing ? (
          <div className="space-y-2">
            <input
              type="text"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              placeholder={originalValue || displayValue}
              className="w-full px-3 py-2 border border-azul-surelia rounded-lg focus:outline-none focus:ring-2 focus:ring-azul-surelia/50"
              autoFocus
            />
            {originalValue && (
              <p className="text-xs text-gray-500">
                Valor original: {originalValue}
              </p>
            )}
            <button
              onClick={() => handleSaveEdit(fieldKey)}
              className="flex items-center gap-1 px-3 py-1.5 bg-azul-surelia text-white rounded-lg hover:bg-azul-surelia/90 transition-colors text-sm"
            >
              <CheckIcon className="h-4 w-4" />
              Aceptar
            </button>
          </div>
        ) : (
          <div>
            <p className="text-gray-700">
              {Array.isArray(fieldValue?.value)
                ? fieldValue.value.map((item, index) => (
                    <span key={index}>
                      {`- ${item}`}
                      <br />
                    </span>
                  ))
                : displayValue}
            </p>
            {originalValue && (
              <p className="text-xs text-gray-500 mt-1">
                (Editado - original: {originalValue})
              </p>
            )}

            {/* Aceptar button in review mode */}
            {isReviewMode && (
              <button
                onClick={handleNextField}
                className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-2 bg-azul-surelia text-white rounded-lg hover:bg-azul-surelia/90 transition-colors"
              >
                <CheckIcon className="h-5 w-5" />
                Aceptar
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  // Navegación entre fuentes
  const handleSourceNavigation = (fieldKey, direction, sources) => {
    setFieldIndices((prev) => {
      const currentIndex = prev[fieldKey];

      // First click - show the current (first) source without navigating yet
      if (currentIndex === undefined) {
        scrollToLines([sources[0]]);
        return { ...prev, [fieldKey]: 0 };
      }

      // Subsequent clicks - navigate
      let newIndex;
      if (direction === 'prev') {
        newIndex = currentIndex === 0 ? sources.length - 1 : currentIndex - 1;
      } else {
        newIndex = currentIndex === sources.length - 1 ? 0 : currentIndex + 1;
      }

      scrollToLines([sources[newIndex]]);
      return { ...prev, [fieldKey]: newIndex };
    });
  };

  // Scroll a líneas resaltadas
  const scrollToLines = (lineIds) => {
    if (lineIds && lineIds.length > 0 && pdfContainerRef.current) {
      const firstLineEl = document.getElementById(`highlight-${lineIds[0]}`);
      if (firstLineEl) {
        firstLineEl.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
      }
      setSelectedLines(lineIds);
    }
  };

  // Render extracted data fields by type
  const renderFieldsByType = () => {
    if (!processedFileData?.results || !localProcessedData?.results) {
      return <Text>No hay datos extraídos.</Text>;
    }

    // Excel type is special - always show full view
    if (fileType === 'estadosFinancierosExcel') {
      // Excel: show raw data and summary tables
      return (
        <div className="space-y-6">
          {/* Raw Data Section */}
          {processedFileData?.results?.sheetData && Object.entries(processedFileData.results.sheetData).length > 0 && (
            <div className="border rounded-lg p-4">
              <Text className="font-medium text-lg mb-4">Datos del Excel</Text>
              <div className="space-y-4">
                {Object.entries(processedFileData.results.sheetData).map(([sheetName, rows]) => (
                  <div key={sheetName} className="border rounded-lg p-4">
                    <Text className="font-medium mb-4">{sheetName}</Text>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        {Array.isArray(rows) && rows.length > 0 && (
                          <>
                            <thead>
                              <tr className="bg-gray-50">
                                {Object.keys(rows[0]).map((header) => (
                                  <th
                                    key={header}
                                    className="px-4 py-2 text-left text-sm font-medium text-gray-900"
                                    style={{ minWidth: '150px' }}
                                  >
                                    {header}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                              {rows.map((row, rowIndex) => (
                                <tr key={rowIndex} className="hover:bg-gray-50">
                                  {Object.entries(row).map(([key, value], cellIndex) => (
                                    <td
                                      key={`${rowIndex}-${cellIndex}`}
                                      className="px-4 py-2 text-sm text-gray-900"
                                    >
                                      {typeof value === 'object'
                                        ? value === null
                                          ? ''
                                          : JSON.stringify(value)
                                        : value ?? ''}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </>
                        )}
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Financial summary tables — years derived from the data, like the info tab */}
          {[
            { key: 'balance_general', label: 'Balance General' },
            { key: 'estado_resultados', label: 'Estado de Resultados' },
            { key: 'conciliacion_fiscal', label: 'Conciliación Fiscal' },
          ].map(({ key: sectionKey, label }) => {
            const section = processedFileData?.results?.financial_summary?.[sectionKey];
            if (!section || Object.keys(section).length === 0) return null;
            const keySet = new Set();
            Object.values(section).forEach((row) => {
              if (row && typeof row === 'object' && !Array.isArray(row)) {
                Object.keys(row).forEach((k) => {
                  if (/^\d+$/.test(k)) keySet.add(k);
                });
              }
            });
            const availableYears = [...keySet];
            const isYearLike = availableYears.length > 0 && availableYears.every((k) => /^\d{4}$/.test(k));
            availableYears.sort((a, b) => (isYearLike ? Number(b) - Number(a) : Number(a) - Number(b)));
            if (availableYears.length === 0) return null;
            return (
              <div key={sectionKey} className="border rounded-lg p-4">
                <Text className="font-medium text-lg mb-4">{label}</Text>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-900" style={{ minWidth: '300px' }}>Concepto</th>
                        {availableYears.map((year) => (
                          <th key={year} className="px-4 py-2 text-right text-sm font-medium text-gray-900" style={{ minWidth: '200px' }}>
                            {/^\d{4}$/.test(year) ? year : String(2024 - Number(year))}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {Object.entries(section).map(([rowKey, value]) => (
                        <tr key={rowKey} className="hover:bg-gray-50">
                          <td className="px-4 py-2 text-sm text-gray-900 font-medium capitalize">
                            {rowKey.split('_').join(' ')}
                          </td>
                          {availableYears.map((year) => (
                            <td key={year} className="px-4 py-2 text-sm text-gray-900 text-right">
                              {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(value[year] || 0)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    // For all other file types, get the appropriate fields
    let fields = getFieldsForFileType();
    if (fields.length === 0) {
      return <Text>No hay campos configurados para este tipo de documento.</Text>;
    }

    // Filter out the "errors" field if there are no errors
    const errorField = localProcessedData?.results?.errors;
    const errorMessage =
      errorField?.errorMessage?.value ||
      errorField?.value ||
      localProcessedData?.errors?.errorMessage?.value ||
      'N/A';
    const hasErrors = errorMessage !== 'N/A' && errorMessage !== '' && errorMessage !== null;

    if (!hasErrors) {
      fields = fields.filter(field => field.value !== 'errors');
    }

    // Filter out optional fields that have no data (e.g. numeroContrato in legacy contracts)
    fields = fields.filter(field => !field.optional || localProcessedData.results?.[field.value]?.value);

    // If file is already reviewed OR bypassReview is true, show all fields
    const isReviewed = localProcessedData?.reviewedBy;
    if (isReviewed || bypassReview) {
      return fields
        .filter((campo) => !campo.optional || localProcessedData.results?.[campo.value]?.value)
        .map((campo) => (
          <React.Fragment key={campo.value}>
            {renderField(campo.name, localProcessedData.results, campo.value)}
          </React.Fragment>
        ));
    }

    // If not reviewed, show one-by-one review mode
    // Make sure currentFieldIndex is within bounds
    const safeCurrentFieldIndex = Math.min(currentFieldIndex, fields.length - 1);
    const currentField = fields[safeCurrentFieldIndex];

    return (
      <div className="space-y-4">
        {/* Progress indicator */}
        <div className="px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm font-medium text-blue-900">
            Revisando campo {safeCurrentFieldIndex + 1} de {fields.length}
          </p>
        </div>

        {/* Already validated fields (shown above) */}
        {fields.map((campo, index) => {
          if (index >= safeCurrentFieldIndex) return null;

          // Only show if checked
          if (checkedFields.has(campo.value)) {
            return (
              <React.Fragment key={campo.value}>
                {renderField(campo.name, localProcessedData.results, campo.value)}
              </React.Fragment>
            );
          }
          return null;
        })}

        {/* Current field being reviewed */}
        {currentField && (
          <div ref={currentFieldRef}>
            {renderField(
              currentField.name,
              localProcessedData.results,
              currentField.value,
              true // Pass flag to indicate review mode
            )}
          </div>
        )}

        {/* Pending fields (shimmers shown below) */}
        {fields.map((campo, index) => {
          if (index <= safeCurrentFieldIndex) return null;

          // Show shimmer for pending fields
          return (
            <div
              key={campo.value}
              className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="h-5 w-32 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded shimmer-animation"></div>
                <div className="h-4 w-16 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded shimmer-animation"></div>
              </div>
              <div className="h-4 w-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded shimmer-animation"></div>
            </div>
          );
        })}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-white z-50 overflow-hidden">
      {/* Header with back button */}
      <div className="bg-azul-surelia text-white px-6 py-4 flex items-center justify-between shadow-lg">
        <div className="flex items-center gap-4">
          <Button
            color="white"
            onClick={onClose}
            className="flex items-center gap-2"
          >
            <ArrowLeftIcon className="h-5 w-5" />
            Volver
          </Button>
          <div>
            <h2 className="text-xl font-semibold">{fileLabel}</h2>
            <p className="text-sm opacity-90">{fileName}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {!isClient && localProcessedData?.reviewedBy && (
            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-green-100 text-green-700 text-sm font-medium">
              ✓ Revisado por {localProcessedData.reviewedBy}
              {localProcessedData?.reviewedAt && (
                <span className="text-green-600 ml-1">
                  el {new Date(localProcessedData.reviewedAt.seconds * 1000).toLocaleDateString("es-MX")}
                </span>
              )}
            </span>
          )}
          {!isClient && linkId && onMarkAsReviewed && onUnmarkAsReviewed && (
            <Button
              color={localProcessedData?.reviewedBy ? "red" : "green"}
              onClick={async () => {
                const fileDocId = localProcessedData?.fileDocId || localProcessedData?.id;
                if (localProcessedData?.reviewedBy) {
                  await onUnmarkAsReviewed(linkId, fileDocId);
                  // Update local state immediately
                  setLocalProcessedData(prev => ({
                    ...prev,
                    reviewedBy: null,
                    reviewedAt: null
                  }));

                  // Log unreview to bitacora
                  try {
                    await logBitacora({
                      action: 'UPDATE',
                      collection: 'links_files',
                      documentId: fileDocId,
                      metadata: {
                        linkId,
                        clientId,
                        clientName: clientName || 'Sin nombre',
                        description: `Marcó documento "${fileLabel || fileName}" como pendiente de revisión`,
                      },
                      user,
                      userData,
                    });
                  } catch (logError) {
                    console.error('Failed to log file unreview to bitacora:', logError);
                  }
                } else {
                  await onMarkAsReviewed(linkId, fileDocId);
                  // Update local state immediately with current user
                  setLocalProcessedData(prev => ({
                    ...prev,
                    reviewedBy: user?.email || 'Usuario',
                    reviewedAt: { seconds: Date.now() / 1000 }
                  }));

                  // Log manual review to bitacora
                  try {
                    await logBitacora({
                      action: 'UPDATE',
                      collection: 'links_files',
                      documentId: fileDocId,
                      metadata: {
                        linkId,
                        clientId,
                        clientName: clientName || 'Sin nombre',
                        description: `Marcó documento "${fileLabel || fileName}" como revisado manualmente`,
                      },
                      user,
                      userData,
                    });
                  } catch (logError) {
                    console.error('Failed to log file review to bitacora:', logError);
                  }
                }
                if (onRefresh) {
                  await onRefresh();
                }
              }}
            >
              {localProcessedData?.reviewedBy ? "Marcar como NO REVISADO" : "Marcar como revisado"}
            </Button>
          )}
        </div>
      </div>

      {/* Main content area */}
      <div className="flex h-[calc(100dvh-80px)] gap-6 p-6">
        {/* Panel de datos extraídos */}
        <div className={`${fileType === 'estadosFinancierosExcel' ? 'flex-1' : 'w-1/3'} border rounded-lg p-6 bg-white overflow-y-auto shadow-lg`}>
          <Text className="font-medium text-lg mb-6">Datos Extraídos</Text>
          <div className="space-y-4">{renderFieldsByType()}</div>
        </div>

        {/* Panel de vista previa PDF */}
        {fileType !== 'estadosFinancierosExcel' && (
          <div className="w-2/3 border rounded-lg bg-white shadow-lg flex flex-col overflow-hidden">
            {/* Static header with controls */}
            <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50">
              <div className="flex items-center gap-3">
                <button
                  onClick={handleZoomOut}
                  className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                  title="Alejar"
                  disabled={scale <= 0.5}
                >
                  <MagnifyingGlassMinusIcon className="h-5 w-5 text-gray-700" />
                </button>
                <button
                  onClick={handleResetZoom}
                  className="px-3 py-1 hover:bg-gray-200 rounded-lg transition-colors text-sm font-medium text-gray-700"
                  title="Restablecer zoom"
                >
                  {Math.round(scale * 100)}%
                </button>
                <button
                  onClick={handleZoomIn}
                  className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                  title="Acercar"
                  disabled={scale >= 3.0}
                >
                  <MagnifyingGlassPlusIcon className="h-5 w-5 text-gray-700" />
                </button>
              </div>
              <button
                onClick={handleDownload}
                className="flex items-center gap-2 px-3 py-2 hover:bg-gray-200 rounded-lg transition-colors"
                title="Descargar PDF"
              >
                <ArrowDownTrayIcon className="h-5 w-5 text-gray-700" />
                <span className="text-sm font-medium text-gray-700">Descargar</span>
              </button>
            </div>
            {/* PDF viewer */}
            <div
              ref={pdfContainerRef}
              className="relative flex-1 overflow-x-auto overflow-y-auto bg-gray-50 p-4"
            >
              <div className="mx-auto" style={{ width: 'fit-content' }}>
                {pdfUrl ? (
                  <Document
                    file={pdfUrl}
                    onLoadSuccess={onDocumentLoadSuccess}
                    className="flex flex-col"
                  >
                  {Array.from(new Array(numPages), (_, index) => {
                    const pageNumber = index + 1;
                    const highlights = processedFileData && processedFileData.lines
                      ? Object.entries(processedFileData.lines).filter(([, line]) => Number(line.page) === pageNumber)
                      : [];
                    return (
                      <div key={pageNumber} className="relative mb-4" style={{ display: 'inline-block' }}>
                        <Page
                          pageNumber={pageNumber}
                          scale={scale}
                        />
                        {highlights.map(([id, line]) => {
                          const padding = 0.006;
                          return (
                            <div
                              id={`highlight-${id}`}
                              key={id}
                              style={{
                                position: 'absolute',
                                border: selectedLines.includes(id)
                                  ? '0px solid #C0EB34'
                                  : '0px solid #340895',
                                backgroundColor: selectedLines.includes(id)
                                  ? 'rgba(192, 235, 52, 0.2)'
                                  : 'rgba(52, 8, 149, 0.2)',
                                left: `${Math.max(0, line.boundingBox.left - padding) * 100}%`,
                                top: `${Math.max(0, line.boundingBox.top - padding) * 100}%`,
                                width: `${Math.min(1, line.boundingBox.width + 2 * padding) * 100}%`,
                                height: `${Math.min(1, line.boundingBox.height + 2 * padding) * 100}%`,
                                pointerEvents: 'none',
                              }}
                            />
                          );
                        })}
                      </div>
                    );
                  })}
                </Document>
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-500">
                    No se pudo cargar el archivo
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
